import React, { useState, memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useQuery } from '@apollo/client';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  vippedRowQuery,
  featuredRowQuery,
  agenciesRowQuery,
  residencesRowQuery
} from '../../../queries';
import Section from '../../Section';
import SectionList from '../../SectionList';
import ItemCard from '../../Cards/ItemCard';
import ResidenceCard from '../../Cards/ResidencesCard/ResidencesHomePageCard';
import PopularRequests from '../../PopularRequests';
import QuickLinksPlaceholder from '../../Placeholders/QuickLinksPlaceholder';
import SectionPlaceholder from '../../Placeholders/SectionPlaceholder';
import ItemCardPlaceholder from '../../Placeholders/ItemCardPlaceholder';
import withEndlessScroll from '../../HOC/endlessScroll';
import { setError } from '../../../actions';

const TopTabs = dynamic(() => import('../../TopTabs'), {
  loading: QuickLinksPlaceholder,
  ssr: false
});

const Banner = dynamic(() => import('../../Banner'), {
  ssr: false
});

const vippedRowQueryVars = {
  limit: 16,
  sort: 'RANDOM'
};

let reciviesItems = 24;

const featuredListRef = React.createRef();
const EndlessFeaturedItems = withEndlessScroll(SectionList);

const mapDispatchToProps = dispatch => {
  return {
    setErrorUI: errorState => dispatch(setError(errorState))
  };
};

const HomePage = ({ setErrorUI }) => {
  const { t } = useTranslation();
  const [itemId, setItemId] = useState(null);
  const setCarousel = id => {
    return itemId === id ? null : setItemId(id);
  };

  /*
   * Load  data via hooks
   * */
  const [
    {
      loading: vippedQueryLoading,
      data: vippedQueryData,
      error: vippedQueryError
    },
    {
      loading: agenciesQueryLoading,
      data: agenciesQueryData,
      error: agenciesQueryError
    },
    {
      loading: residencesQueryLoading,
      data: residencesQueryData,
      error: residencesQueryError
    },
    {
      loading: featuredQueryLoading,
      data: featuredQueryData,
      error: featuredQueryError,
      fetchMore
    }
  ] = (() => {
    const vippedQuery = useQuery(vippedRowQuery(vippedRowQueryVars), {
      variables: vippedRowQueryVars,
      ssr: false,
      fetchPolicy: 'network-only'
    });
    const agenciesQuery = useQuery(agenciesRowQuery, {
      ssr: false,
      fetchPolicy: 'network-only'
    });
    const residencesQuery = useQuery(residencesRowQuery, {
      ssr: false,
      fetchPolicy: 'network-only'
    });
    const featuredQuery = useQuery(
      featuredRowQuery({
        limit: 24,
        offset: 0
      }),
      {
        variables: {
          limit: 24,
          offset: 0
        },
        ssr: false,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first'
      }
    );

    return [vippedQuery, agenciesQuery, residencesQuery, featuredQuery];
  })();

  /*
   * Vipped List
   * */
  const itemsVipped = vippedQueryData?.items || [];
  const itemsVippedList =
    itemsVipped &&
    itemsVipped.map(item => (
      <ItemCard
        key={item.id}
        carousel={false}
        cypress="vipped-card-link"
        {...item}
      />
    ));

  /*
   * Agencies List
   * */
  const agencies = agenciesQueryData?.agencies || [];
  const agenciesList = agencies
    .filter(a => a.items.length > 0)
    .map(agency => (
      <ItemCard
        key={agency.items[0].id}
        carousel={false}
        cypress="agency-card-link"
        {...agency.items[0]}
      />
    ));

  /*
   * Residences List
   * */
  const residences = residencesQueryData?.residences || [];
  const residencesList =
    residences &&
    residences.map(residence => (
      <ResidenceCard
        key={residence.id}
        {...residence}
        cypress="residence-card-link"
      />
    ));

  /*
   * Featured List
   * */
  const items = featuredQueryData?.items || [];
  const itemsList =
    items &&
    items.map(item => (
      <ItemCard
        key={item.id}
        {...item}
        itemId={itemId}
        setCarousel={setCarousel}
        carousel={true}
        cypress="featured-card-link"
        cypress-card="featured-card"
      />
    ));

  const itemsWithBanner = itemsList.reduce((list, currItem, currIndex) => {
    const isTenthElement = currIndex === 9;
    const bottomBanner = (
      <Banner
        key={'bottom-banner-1'}
        tagName={'p'}
        containerClassName={'lotriver-bottom-banner content-banner'}
        containerId={'js-lotriver-bottom-banner'}
        sid={'220686'}
        bn={'6'}
        arCn={2}
      />
    );
    return isTenthElement
      ? [...list, currItem, bottomBanner]
      : [...list, currItem];
  }, []);

  useEffect(() => {
    return () => {
      reciviesItems = 24;
    };
  }, []);

  useEffect(() => {
    if (
      vippedQueryError ||
      agenciesQueryError ||
      residencesQueryError ||
      featuredQueryError
    ) {
      setErrorUI(true);
    }
  }, [
    vippedQueryError,
    agenciesQueryError,
    residencesQueryError,
    featuredQueryError
  ]);

  return (
    <>
      <TopTabs />
      {!vippedQueryLoading && itemsVippedList ? (
        <Section
          id="home-page-vipped"
          title={t('sections.vipped.title')}
          url="/items/vipped"
          urlTitle={t('sections.vipped.show_all')}
          sectionAdditionalClasses="section-block"
          cypress="vipped-section-title"
        >
          <SectionList listAdditionalClasses="section-list--horizontal-scroll">
            {itemsVippedList}
          </SectionList>
        </Section>
      ) : (
        <SectionPlaceholder>
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
        </SectionPlaceholder>
      )}
      <Banner
        key="lotriver-top-banner"
        containerClassName={'lotriver-top-banner'}
        containerId={'js-lotriver-top-banner'}
        sid={'220686'}
        bn={'2'}
        arCn={1}
      />
      {!agenciesQueryLoading && agenciesList ? (
        <Section
          id="home-page-agencies"
          title={t('sections.agencies.title')}
          url="/agencies"
          asLink="/agentlikler"
          urlTitle={t('sections.agencies.show_all')}
          sectionAdditionalClasses="section-block"
          cypress="agencies-section-title"
        >
          <SectionList listAdditionalClasses="section-list--horizontal-scroll">
            {agenciesList}
          </SectionList>
        </Section>
      ) : (
        <SectionPlaceholder>
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
        </SectionPlaceholder>
      )}
      {!residencesQueryLoading && residencesList ? (
        <Section
          id="home-page-residences"
          title={t('sections.residences.title')}
          url="/residences"
          asLink="/yasayis-kompleksleri"
          urlTitle={t('sections.residences.show_all')}
          sectionAdditionalClasses="section-block center-block"
          cypress="residences-section-title"
        >
          <SectionList listAdditionalClasses="section-list_hide-excess-items">
            {residencesList}
          </SectionList>
        </Section>
      ) : (
        <SectionPlaceholder>
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
        </SectionPlaceholder>
      )}

      {!(featuredQueryLoading && !itemsList.length) ? (
        <Section
          id="home-page-featured"
          title={t('sections.featured.title')}
          url="/items/vipped"
          urlTitle={t('sections.vipped.show_all')}
          sectionAdditionalClasses="section-block center-block"
          rightBlock={
            <h1
              className="thumblist-premium-title"
              data-cy="featured-extra-title"
            >
              <Trans i18nKey="sections.featured.right_text" />
            </h1>
          }
          cypress="featured-section-title"
        >
          <EndlessFeaturedItems
            items={itemsWithBanner}
            loading={featuredQueryLoading}
            ref={featuredListRef}
            isStopLoad={reciviesItems < 24}
            onLoadMore={() => {
              const currentLength = items.length;

              fetchMore({
                query: featuredRowQuery({
                  limit: 24,
                  offset: currentLength
                }),
                variables: {
                  limit: 24,
                  offset: currentLength
                },
                notifyOnNetworkStatusChange: true
              }).then(fetchMoreResult => {
                reciviesItems = fetchMoreResult.data.items.length;
              });
            }}
          >
            {itemsWithBanner}
          </EndlessFeaturedItems>
        </Section>
      ) : (
        <SectionPlaceholder>
          <ItemCardPlaceholder />
          <ItemCardPlaceholder />
        </SectionPlaceholder>
      )}
      <PopularRequests />
    </>
  );
};

HomePage.propTypes = {
  setErrorUI: PropTypes.func
};

export default connect(null, mapDispatchToProps)(memo(HomePage));
